import {useEffect, useState} from 'react';
import http from 'services/http';
import PermissionGate from '../../utils/PermissionGate';
import {alerta} from '../Alert';
import styles from './styles.module.css';

/**
 * Aba "Bloquear" da extração: limite de venda por modalidade.
 *
 * O cálculo é o mesmo da tela de descarga — Premiação (R$) ÷ cotação = quanto
 * pode ser vendido em cada modalidade — mas o efeito é outro: aqui a aposta que
 * passa do limite não é repassada para a banca de descarga, ela é barrada na
 * venda do app.
 *
 * Campo em 0,00 significa SEM LIMITE (não bloqueia nada) — é o inverso da
 * descarga, onde 0 manda tudo embora.
 *
 * @param {Object} props
 * @param {number} props.id - ID da loteria
 */

const naoMostrar = [
    'Milhar Brinde', 'Milhar Moto', 'Unidade',
    'Duque de Grupo', 'Terno de Grupo', 'Duque de Dezena', 'Duque de Dezena F.M.F',
    'Terno de Dezena', 'Terno de Dezena F.M.F', 'Passe', 'Passe vai e vem',
    'Milhar e Centena', 'Milhar Invertida', 'MC Invertida',
    'Centena Invertida'
];

const ordemModalidades = [
    'Milhar', 'Centena', 'Dezena', 'Grupo',
    'Duque de Dezena 1/2', 'Duque de Dezena 1/5',
    'Duque de Dezena F.M.F 1/2', 'Duque de Dezena F.M.F 1/5',
    'Duque de Grupo 1/2', 'Duque de Grupo 1/5',
    'Terno de Dezena 1/3', 'Terno de Dezena 1/5', 'Terno de Dezena 1/10',
    'Terno de Dezena F.M.F 1/3', 'Terno de Dezena F.M.F 1/5', 'Terno de Dezena F.M.F 1/10',
    'Terno de Grupo 1/3', 'Terno de Grupo 1/5', 'Terno de Grupo 1/10',
    'Passe 1/2', 'Passe 1/5', 'Passe vai e vem 1/2', 'Passe vai e vem 1/5'
];

const deveMostrar = (nome) => {
    if (naoMostrar.includes(nome)) return false;
    if (nome.toLowerCase().includes('combinado')) return false;
    return true;
};

const ordenarModalidades = (modalidades) =>
    [...modalidades].sort((a, b) => {
        const ia = ordemModalidades.indexOf(a.nome);
        const ib = ordemModalidades.indexOf(b.nome);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

const formatarValorExistente = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '0,00';
    const num = parseFloat(String(valor).replace(',', '.'));
    return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
};

const formatarValorMonetario = (valor) => {
    const digits = String(valor).replace(/\D/g, '');
    return (parseInt(digits || '0', 10) / 100).toFixed(2).replace('.', ',');
};

const paraNumero = (valor) => {
    const num = parseFloat(String(valor).replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
};

export default function LimitesBloqueioLoteria({id}) {
    const [modalidades, setModalidades] = useState([]);
    const [premiacao, setPremiacao] = useState('');
    const [cotacoes, setCotacoes] = useState([]);
    const [cotacaoSelecionada, setCotacaoSelecionada] = useState('');
    const [camposBloqueados, setCamposBloqueados] = useState(true);
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (id) {
            requestLimites();
            requestCotacoes();
        }
    }, [id]);

    async function requestLimites() {
        try {
            setCarregando(true);
            const response = await http.get(`/loterias/${id}/bloqueio`);
            const {data} = response.data;

            setModalidades((data.modalidades ?? []).map((m) => ({
                ...m,
                limite: formatarValorExistente(m.limite)
            })));

            if (data.premiacao) setPremiacao(formatarValorExistente(data.premiacao));
            if (data.cotacao) setCotacaoSelecionada(data.cotacao);
        } catch (e) {
            alerta(e.response?.data);
        } finally {
            setCarregando(false);
        }
    }

    async function requestCotacoes() {
        try {
            const response = await http.get('bancas/titulo-cotacoes');
            const {data} = response.data;
            setCotacoes(data);
        } catch (e) {
            console.log('Erro ao buscar cotações:', e);
        }
    }

    const extrairValoresCotacao = (nomeCotacao) => {
        if (!nomeCotacao) return {M: 0, C: 0, D: 0, G: 0};
        const match = nomeCotacao.match(/M:\s*(\d+)\s*\/\s*C:\s*(\d+)\s*\/\s*D:\s*(\d+)\s*\/\s*G:\s*(\d+)/);
        return match
            ? {M: parseInt(match[1]), C: parseInt(match[2]), D: parseInt(match[3]), G: parseInt(match[4])}
            : {M: 0, C: 0, D: 0, G: 0};
    };

    const mapearModalidadeParaCotacao = (nome) => {
        const n = nome.toLowerCase();
        if (n.includes('milhar')) return 'M';
        if (n.includes('centena')) return 'C';
        if (n.includes('dezena')) return 'D';
        if (n.includes('grupo')) return 'G';
        return null;
    };

    function calcular() {
        if (!premiacao || !cotacaoSelecionada) {
            alerta({error: true, message: 'Preencha a premiação e selecione uma cotação'});
            return;
        }

        const cotacao = cotacoes.find((c) => c.id === parseInt(cotacaoSelecionada));
        if (!cotacao) return;

        const valores = extrairValoresCotacao(cotacao.nome);
        const premiacaoNum = paraNumero(premiacao);

        if (premiacaoNum <= 0) {
            alerta({error: true, message: 'Digite um valor válido para premiação'});
            return;
        }

        const arr = [...modalidades];
        arr.forEach((mod, idx) => {
            const tipo = mapearModalidadeParaCotacao(mod.nome);
            if (tipo && valores[tipo] > 0) {
                arr[idx].limite = (Math.floor((premiacaoNum / valores[tipo]) * 100) / 100)
                    .toFixed(2)
                    .replace('.', ',');
            }
        });

        setModalidades(arr);
        setCamposBloqueados(false);
    }

    function handleChangeLimite(event) {
        const {dataset: {index}, value} = event.target;
        const arr = [...modalidades];
        arr[index].limite = formatarValorMonetario(value);
        setModalidades(arr);
    }

    async function salvar() {
        try {
            setSalvando(true);

            const response = await http.put(`/loterias/${id}/bloqueio`, {
                premiacao: premiacao ? paraNumero(premiacao) : null,
                cotacao: cotacaoSelecionada ? parseInt(cotacaoSelecionada) : null,
                limites: modalidades.map((m) => ({tipo: m.tipo, limite: paraNumero(m.limite)}))
            });

            alerta(response.data);
            setCamposBloqueados(true);
        } catch (e) {
            alerta(e.response?.data);
        } finally {
            setSalvando(false);
        }
    }

    if (carregando) return <div className="loader" />;

    return (
        <div className={styles.bloqueio}>
            <div className={styles.titulo}>
                <span>Valores do bloqueio</span>
            </div>

            <div className={styles.calculo}>
                <div className={styles.campo}>
                    <label>Premiação (R$):</label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        placeholder="0,00"
                        value={premiacao}
                        onChange={(e) => setPremiacao(formatarValorMonetario(e.target.value))}
                        style={{width: '150px'}}
                    />
                </div>

                <div className={styles.campo}>
                    <label>Cotação:</label>
                    <select
                        className="form-control"
                        value={cotacaoSelecionada}
                        onChange={(e) => setCotacaoSelecionada(e.target.value)}
                        style={{minWidth: '250px'}}
                    >
                        <option value="">Selecione uma cotação</option>
                        {cotacoes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                </div>

                <button className="btn btn-primary" onClick={calcular} style={{height: '38px'}}>
                    Calcular
                </button>
            </div>

            <div className={styles.ajuda}>
                Valor máximo que esta extração aceita vender no mesmo número, na mesma
                modalidade e no mesmo sorteio, somando todos os cambistas do dia.
                Campo em <strong>0,00</strong> = sem limite.
            </div>

            <div className={styles.lista}>
                {ordenarModalidades(modalidades.map((m, i) => ({...m, originalIndex: i})))
                    .filter((m) => deveMostrar(m.nome))
                    .map((m) => (
                        <div key={m.tipo}>
                            <label htmlFor={`bloqueio-${m.tipo}`}>{m.nome}</label>
                            <input
                                onChange={handleChangeLimite}
                                disabled={camposBloqueados}
                                id={`bloqueio-${m.tipo}`}
                                data-index={m.originalIndex}
                                type="text"
                                inputMode="numeric"
                                className="form-control"
                                value={m.limite}
                                placeholder="0,00"
                            />
                        </div>
                    ))}
            </div>

            <PermissionGate permissions={['Alterar loteria']}>
                <div className={styles.botoes}>
                    {camposBloqueados ? (
                        <button className="btn btn-info" onClick={() => setCamposBloqueados(false)} disabled={salvando}>
                            Editar
                        </button>
                    ) : (
                        <button className="btn btn-secondary" onClick={() => setCamposBloqueados(true)} disabled={salvando}>
                            Desabilitar
                        </button>
                    )}
                    <button className="btn btn-success" onClick={salvar} disabled={salvando}>
                        Salvar
                    </button>
                </div>
            </PermissionGate>
        </div>
    );
}
